import { queryOptions, useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { refreshLogin } from "@/common/services/auth/Auth.service";
import { env } from "@/env";
import {
  eagriFarmName,
  registerWithShapeFile,
  searchCountries,
  storeAuthData,
  validateShapefile as generatedValidateShapefile,
} from "@/generated/api/agroevidence";
import type { CountryTo } from "@/generated/api/agroevidence";
import { http } from "@/shared/api/client";
import type { HttpResponse } from "@/shared/api/client";

const SHAPEFILE_INVALID_MESSAGE_ID = "validation.fileNotValid";
const SHAPEFILE_OVERLAP_MESSAGE_ID = "validation.fileNotValidOverlap";
const SHAPEFILE_CRS_MESSAGE_ID = "validation.fileNotValidCrs";

const shapefileErrorCodeSchema = z.enum([
  "/agroevidence/shapefile/overlapping-geometries",
  "/agroevidence/shapefile/crs-not-found",
  "/agroevidence/shapefile/invalid-metadata",
]);

const shapefileRequestFailureSchema = z.object({
  response: z.object({
    data: z.object({ error: shapefileErrorCodeSchema }),
  }),
});

const SHAPEFILE_ERROR_MESSAGE_IDS: Record<
  z.infer<typeof shapefileErrorCodeSchema>,
  string
> = {
  "/agroevidence/shapefile/overlapping-geometries":
    SHAPEFILE_OVERLAP_MESSAGE_ID,
  "/agroevidence/shapefile/crs-not-found": SHAPEFILE_CRS_MESSAGE_ID,
  "/agroevidence/shapefile/invalid-metadata": SHAPEFILE_CRS_MESSAGE_ID,
};

type Country = {
  code: string;
  fullSupport: boolean;
  name: string;
};

type EagriAuthVariables = {
  login: string;
  secret: string;
  szrid: string;
};

type CreateFarmVariables = {
  countryCode: string;
  farmName: string;
};

type ValidateShapefileVariables = {
  countryCode: string;
  file: File;
};

type ImportShapefileVariables = CreateFarmVariables & {
  file: File;
};

type RequestFailure = {
  response?: {
    status?: number;
    data?: { errors?: { code?: string }[] };
  };
};

const signupKeys = {
  all: ["signup"] as const,
  countries: (search?: string) =>
    [...signupKeys.all, "countries", search] as const,
  eagriFarmName: () => [...signupKeys.all, "eagri-farm-name"] as const,
};

const countriesQuery = (search?: string) =>
  queryOptions({
    queryKey: signupKeys.countries(search),
    queryFn: async ({ signal }) => {
      const response = await searchCountries({ search }, { signal });
      return response.data.map(toCountry);
    },
  });

const eagriFarmNameQuery = () =>
  queryOptions({
    queryKey: signupKeys.eagriFarmName(),
    queryFn: async ({ signal }) => {
      const response = await eagriFarmName({ signal });
      return response.data.farmName ?? "";
    },
  });

// silent: the caller maps the eAGRI HTTP status onto one of two specific messages
const useStoreEagriAuthMutation = () =>
  useMutation({
    mutationFn: (credentials: EagriAuthVariables) => storeAuthData(credentials),
    meta: { silent: true },
  });

const useCreateEmptyFarmMutation = () =>
  useMutation({
    mutationFn: async ({ countryCode, farmName }: CreateFarmVariables) => {
      await createEmptyFarm(farmName, countryCode);
      await refreshLogin();
    },
    meta: { errorMessageId: "validation.submitError" },
  });

const useCreateEagriFarmMutation = () =>
  useMutation({
    mutationFn: (farmName: string) => createEagriFarm(farmName),
    meta: { errorMessageId: "validation.submitError" },
  });

// silent: the result is form state (valid / invalid file), not an error toast
const useValidateShapefileMutation = () =>
  useMutation({
    mutationFn: ({ countryCode, file }: ValidateShapefileVariables) =>
      validateShapefile(countryCode, file),
    meta: { silent: true, skipInvalidation: true },
  });

const useImportShapefileMutation = () =>
  useMutation({
    mutationFn: async ({
      countryCode,
      farmName,
      file,
    }: ImportShapefileVariables) => {
      await importShapefile(countryCode, farmName, file);
      await refreshLogin();
    },
    meta: { errorMessageId: "validation.submitError" },
  });

const getEagriAuthErrorMessageId = (error: unknown): string => {
  const status = (error as RequestFailure)?.response?.status;

  if (status === 503) {
    return "eagri.unavailable";
  }

  if (status === 403) {
    return "eagri.forbidden";
  }

  return "validation.submitError";
};

const getRegistrationErrorMessageId = (error: unknown): string => {
  const response = (error as RequestFailure)?.response;
  const isValidationFailure =
    response?.status === 400 || response?.status === 422;

  if (
    isValidationFailure &&
    response?.data?.errors?.some((item) => item.code === "GatewayEmail")
  ) {
    return "NewAccount.emailFormatError";
  }

  return "validation.submitError";
};

const getShapefileValidationErrorMessageId = (error: unknown): string => {
  const failure = shapefileRequestFailureSchema.safeParse(error);

  return failure.success
    ? SHAPEFILE_ERROR_MESSAGE_IDS[failure.data.response.data.error]
    : SHAPEFILE_INVALID_MESSAGE_ID;
};

const createEmptyFarm = (
  farmName: string,
  countryCode: string,
): Promise<HttpResponse<void>> =>
  http.post<void>(registrationUrl("/empty"), undefined, {
    params: { countryCode, farmName },
  });

const createEagriFarm = (farmName: string): Promise<HttpResponse<void>> =>
  http.post<void>(registrationUrl("/eagri"), undefined, {
    params: { countryCode: "CZ", farmName },
  });

const validateShapefile = async (
  countryCode: string,
  file: File,
): Promise<void> => {
  await generatedValidateShapefile({ countryCode }, { lpisFile: file });
};

const importShapefile = async (
  countryCode: string,
  farmName: string,
  file: File,
): Promise<void> => {
  await registerWithShapeFile({ countryCode, farmName }, { lpisFile: file });
};

const toCountry = (country: CountryTo): Country => ({
  code: country.code,
  fullSupport: country.fullSupport,
  name: country.name,
});

const registrationUrl = (path: string): string =>
  `${env.AGROEVIDENCE_URL}/api/registration${path}`;

export {
  SHAPEFILE_INVALID_MESSAGE_ID,
  signupKeys,
  countriesQuery,
  eagriFarmNameQuery,
  useStoreEagriAuthMutation,
  useCreateEmptyFarmMutation,
  useCreateEagriFarmMutation,
  useValidateShapefileMutation,
  useImportShapefileMutation,
  getEagriAuthErrorMessageId,
  getRegistrationErrorMessageId,
  getShapefileValidationErrorMessageId,
  type Country,
  type EagriAuthVariables,
};
